'use client';

import { formatPrice } from '@/lib/data/products';

/**
 * Renders a price, showing the original amount struck through next to the
 * reduced one whenever the item is discounted — either by a sale price list or
 * by an active automatic promotion (`info.onSale`). `info` is the object
 * returned by `getPriceInfo` / `getProductPriceInfo` / `getVariantPriceInfo`.
 *
 * When `showBadge` is set, a small "−X%" flag is added so the saving is
 * immediately clear and traceable for the customer on grids and cards.
 */
export default function PriceTag({
    info,
    locale = 'de',
    className = '',
    originalClassName = '',
    saleClassName = '',
    showBadge = false,
    badgeLabel,
    badgeClassName = '',
}) {
    if (!info || info.amount == null) return null;

    const current = formatPrice(info.amount, info.currencyCode, locale);

    if (!info.onSale) {
        return <span className={className}>{current}</span>;
    }

    const original = formatPrice(info.originalAmount, info.currencyCode, locale);
    const pct =
        Number(info.originalAmount) > 0
            ? Math.round((1 - Number(info.amount) / Number(info.originalAmount)) * 100)
            : 0;
    const badge = badgeLabel ?? (pct > 0 ? `−${pct}%` : null);

    return (
        <span className={className}>
            <span className={`line-through opacity-50 ${originalClassName}`}>
                {original}
            </span>{' '}
            <span className={saleClassName}>{current}</span>
            {showBadge && badge && (
                <span
                    className={
                        badgeClassName ||
                        'ml-2 inline-block align-middle rounded-sm bg-[#D48937] px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-white'
                    }
                >
                    {badge}
                </span>
            )}
        </span>
    );
}
